import { Injectable, inject } from '@angular/core'

import { Store } from '@ngrx/store'
import { createEffect, createStore } from 'effector'

import { EMPTY, lastValueFrom, of, switchMap, throwError } from 'rxjs'
import { catchError, first, tap } from 'rxjs/operators'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import { BpmnEditorEvents } from '@flowx/designer/modules/bpmn-editor/store/bpmn-editor.events'
import { SwimlaneSummary } from '@flowx/designer/modules/core/dictionary/swimlane.dictionary'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'
import { AreasEvents } from '@flowx/designer/shared/effector/area/area.events'
import { Area, AreaPlatform, ReorderAreas } from '@flowx/designer/shared/entities/nav-area.entity'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import * as NodeActions from '@flowx/designer/shared/store/actions/node.actions'
import { isHttpConflictStatusCode } from '@flowx/shared/utils'

import { AreasApiService, PasteAreasPayload } from '../../api/area.api'

@Injectable({
  providedIn: 'root',
})
export class AreasEffects {
  #flxModalService = inject(FlxModalService)
  #toastService = inject(ToastMessageService)
  #areasApi = inject(AreasApiService)
  #ngrxStore = inject(Store)
  readonly loadAreasFx = createEffect('loadAreasFx').use(({ processVersionId, platform }) =>
    lastValueFrom(this.#areasApi.getAreas(processVersionId, platform))
  )
  readonly createAreaFx = createEffect('createAreaFx').use(
    ({ processVersionId, platform, newArea }) =>
      lastValueFrom(this.#areasApi.createArea(processVersionId, platform, newArea))
  )
  readonly createParentAreaFx = createEffect('createAreaParentFx').use(
    ({ processVersionId, platform, newArea }) =>
      lastValueFrom(this.#areasApi.createParentArea(processVersionId, platform, newArea))
  )
  readonly removeAreaFx = createEffect<{ area: Area; platform: AreaPlatform }, Area>(
    'removeAreaFx'
  ).use(({ area, platform }: { area: Area; platform: AreaPlatform }) => {
    const isNodeNavArea = Area.isNodeNavigationArea(area.type)
    if ((Area.isNavigationArea(area.type) && area.children?.length) || isNodeNavArea) {
      const boldLabel = `<span class="font-bold">${area.label}</span>`
      return lastValueFrom(
        this.#flxModalService
          .show(
            ConfirmationModalComponent,
            {
              closeOnBackdropClick: false,
              backdropClass: 'overlay-backdrop',
            },
            {
              title: isNodeNavArea
                ? `Are you sure you want to remove ${boldLabel} from Area?`
                : `Delete area ${boldLabel}?`,
              message: isNodeNavArea
                ? 'User task will be moved to the User Tasks section'
                : 'Child Areas will be also deleted. User tasks contained within will be moved to the User Tasks section.',
              btnOkText: isNodeNavArea ? 'Remove node' : 'Delete',
            }
          )
          .data$.pipe(
            first(),
            switchMap((data) => {
              if (data.confirm) {
                if (isNodeNavArea) {
                  this.#ngrxStore.dispatch(
                    NodeActions.updateNodeByParentNavAreaId({
                      parentNavAreaId: area.id,
                      isNavAreaAssigned: false,
                    })
                  )
                }
                return this.#areasApi.removeArea(area, platform)
              }
              return of(null)
            })
          )
      )
    } else {
      return lastValueFrom(this.#areasApi.removeArea(area, platform))
    }
  })

  readonly removeParentProcessAreaFx = createEffect<
    { area: Area; platform: AreaPlatform; processVersionId: number },
    void
  >('removeAreaFx').use(
    ({
      area,
      platform,
      processVersionId,
    }: {
      area: Area
      platform: AreaPlatform
      processVersionId: number
    }) =>
      lastValueFrom(
        this.#areasApi.removeParentProcessArea(area, processVersionId, platform).pipe(
          catchError((error) => {
            if (isHttpConflictStatusCode(error?.status)) {
              return this.#flxModalService
                .show(
                  ConfirmationModalComponent,
                  {
                    closeOnBackdropClick: true,
                    backdropClass: 'overlay-backdrop',
                  },
                  {
                    title: error?.error?.title,
                    btnOkText: `Yes, proceed`,
                  }
                )
                .data$.pipe(
                  switchMap(({ confirm }) => {
                    if (confirm) {
                      AreasEvents.removeParentProcessArea({ ...area, force: true })
                    }
                    return EMPTY
                  })
                )
            } else {
              this.#toastService.showErrorMessage(error?.error?.title)
              return EMPTY
            }
          })
        )
      )
  )
  readonly updateAreaSettingsFx = createEffect<Partial<Area>, Partial<Area>, Area>(
    'updateAreaSettingsFx'
  ).use((area: Area) =>
    lastValueFrom(
      this.#areasApi.updateAreaSettings({
        id: area.id,
        label: area.label,
        navigationFlowIds: area.navigationFlowIds,
      })
    )
  )
  readonly reorderAreasFx = createEffect<
    {
      processVersionId: number
      platform: AreaPlatform
      payload: ReorderAreas
    },
    ReorderAreas
  >('reorderAreasFx').use(({ processVersionId, platform, payload }) =>
    lastValueFrom(
      this.#areasApi.reorderAreas(processVersionId, platform, payload).pipe(
        catchError((error) => {
          if (isHttpConflictStatusCode(error?.status)) {
            return this.#flxModalService
              .show(
                ConfirmationModalComponent,
                {
                  closeOnBackdropClick: true,
                  backdropClass: 'overlay-backdrop',
                },
                {
                  title: error?.error?.title,
                  btnOkText: `Yes, proceed`,
                }
              )
              .data$.pipe(
                switchMap(({ confirm }) => {
                  if (confirm) {
                    AreasEvents.reorderAreas({ ...payload, force: true })
                  }
                  return EMPTY
                })
              )
          } else {
            this.#toastService.showErrorMessage(error?.error?.title)
            return EMPTY
          }
        })
      )
    )
  )

  readonly pasteAreasFx = createEffect<
    {
      processVersionId: number
      payload: PasteAreasPayload
    },
    { swimlaneDefinitionId: SwimlaneSummary['id']; userTasks: Area[]; navigationTree: Area[] }
  >('pasteAreasFx').use(({ processVersionId, payload }) =>
    lastValueFrom(
      this.#areasApi.pasteAreas(processVersionId, payload).pipe(
        tap(() => {
          this.#toastService.showSuccessMessage('Areas pasted successfully')
        }),
        catchError((error) => {
          if (isHttpConflictStatusCode(error?.status)) {
            return this.#flxModalService
              .show(
                ConfirmationModalComponent,
                {
                  closeOnBackdropClick: true,
                  backdropClass: 'overlay-backdrop',
                },
                {
                  title: error?.error?.title,
                  btnOkText: `Yes, proceed`,
                }
              )
              .data$.pipe(
                switchMap(({ confirm }) => {
                  if (confirm) {
                    AreasEvents.pasteAreas({ ...payload, force: true })
                  }
                  return EMPTY
                })
              )
          } else {
            return throwError(() => error)
          }
        })
      )
    )
  )

  readonly showErrorToastFx = createEffect('showErrorToastFx').use((error: any) => {
    this.#toastService.showErrorMessage(error?.error?.title)
  })

  readonly loadedAreas$ = createStore<boolean>(false, {
    name: 'loadedAreas$',
  })
    .on(this.loadAreasFx.done, () => true)

    .reset([
      AreasEvents.cleanupAreas,
      BpmnEditorEvents.processVersionChanged,
      AreasEvents.platformSelected,
    ])
}
